import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { GradientLoadingBox } from 'edrolo-style-guide';
import { useEffect, useRef, useState } from 'react';
import Draggable from './Draggable';
import DropZone from './DropZone';
import styles from './styles.scss';
import { useTouchScrollLock } from './useTouchScrollLock';
/* eslint-disable react/prop-types */ // TODO: add props

const DraggablePlaceholder = ({ imageWidth, imageHeight, annotation }) => (
  <div
    style={{
      width: `${(imageWidth / 100) * annotation.geometry.width}px`,
      height: `${(imageHeight / 100) * annotation.geometry.height}px`,
      top: `${(imageHeight / 100) * annotation.geometry.y}px`,
      left: `${(imageWidth / 100) * annotation.geometry.x}px`,
      position: `absolute`,
      background: `white`,
      zIndex: `1`,
    }}
  />
);

const DragAndDropArea = ({
  draggableAnnotations,
  dropzoneAnnotations,
  imageUrl,
  draggableToContainingDropzoneMap,
  onDraggableRelocated,
  dndCorrectnessMap = null,
}) => {
  const containerRef = useRef();
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  useTouchScrollLock(isDragging);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      setContainerWidth(width);
    }
  }, [containerRef]);

  const onImgLoad = ({ target: img }) => {
    setImageWidth(img.naturalWidth);
    setImageHeight(img.naturalHeight);
    setLoading(false);
  };

  const imageRatio = imageWidth / imageHeight;
  const scaledWidth = containerWidth;
  const scaledHeight = containerWidth / imageRatio;

  const handleDragEnd = ({ over, active, ...event }) => {
    onDraggableRelocated(active.id, over?.id);
    setIsDragging(false);
  };

  const draggablesNotInDropZones = draggableAnnotations.filter(
    (annotation) => !draggableToContainingDropzoneMap[annotation.id] // absent or undefined
  );

  const shouldDisableDnD = !!dndCorrectnessMap;

  return (
    <div ref={containerRef}>
      <DndContext
        onDragEnd={handleDragEnd}
        sensors={sensors}
        onDragStart={() => setIsDragging(true)}
        onDragCancel={() => setIsDragging(false)}
      >
        <img onLoad={onImgLoad} src={imageUrl} className={styles.imageLoader} />
        {loading ? (
          <GradientLoadingBox className={styles.loaderBox} />
        ) : (
          <div
            style={{
              position: `relative`,
              backgroundImage: `url("${imageUrl}")`,
              backgroundRepeat: `no-repeat`,
              width: `${scaledWidth}px`,
              height: `${scaledHeight}px`,
              backgroundSize: `${scaledWidth}px ${scaledHeight}px`,
            }}
          >
            {draggablesNotInDropZones.map((annotation) => (
              <Draggable
                key={`draggable-${annotation.id}`}
                id={annotation.id}
                annotation={annotation}
                imageWidth={scaledWidth}
                imageHeight={scaledHeight}
                imageUrl={imageUrl}
                shouldDisableDnD={shouldDisableDnD}
              />
            ))}

            {draggableAnnotations.map((annotation) => (
              <DraggablePlaceholder
                key={`cover-${annotation.id}`}
                annotation={annotation}
                imageWidth={scaledWidth}
                imageHeight={scaledHeight}
              />
            ))}

            {dropzoneAnnotations.map((dropA) => {
              const draggablesInThisDropZone = draggableAnnotations.filter(
                (dragA) =>
                  draggableToContainingDropzoneMap[dragA.id] === dropA.id
              );

              return (
                <DropZone
                  key={dropA.id}
                  annotation={dropA}
                  renderDropped={(draggableAnnotation) => (
                    <Draggable
                      key={draggableAnnotation.id}
                      id={draggableAnnotation.id}
                      annotation={draggableAnnotation}
                      imageWidth={scaledWidth}
                      imageHeight={scaledHeight}
                      imageUrl={imageUrl}
                      isCorrect={
                        dndCorrectnessMap &&
                        dndCorrectnessMap[draggableAnnotation.id]
                      }
                      shouldDisableDnD={shouldDisableDnD}
                      isInDropzone
                    />
                  )}
                  imageWidth={scaledWidth}
                  imageHeight={scaledHeight}
                  draggableAnnotationsInThisDropZone={draggablesInThisDropZone}
                />
              );
            })}
          </div>
        )}
      </DndContext>
    </div>
  );
};

export default DragAndDropArea;
